import { usePublicMetadata } from '../seo/PublicMetadata.jsx';
import { useInitialPublicData } from '../seo/PublicBootstrap.jsx';
import { InlineFeedback } from '@mts/ui/feedback';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Clock, Search } from 'lucide-react';
import { PublicContainer } from '@mts/ui';
import { fetchBlogCategory, fetchBlogPosts } from '../services/content';
import { usePublicTenant } from '../hooks/usePublicTenant';
import { buildPublicPageMetadata } from '../utils/publicMetadata.js';
import { buildBreadcrumbSchema, buildItemListSchema, buildWebPageSchema } from '../utils/structuredData.js';

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' });
}

function BlogCard({ post }) {
  return (
    <article className="overflow-hidden rounded-lg border border-[var(--border,#e2e8f0)] bg-[var(--surface,#fff)] shadow-sm">
      {post.coverImage?.publicUrl ? (
        <Link to={`/blog/${post.slug}`} className="block">
          <img src={post.coverImage.publicUrl} alt={post.coverImage.altText || ''} className="aspect-[16/9] w-full object-cover" />
        </Link>
      ) : null}
      <div className="p-5">
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-[var(--muted,#64748b)]">
          {(post.tags || []).filter(tag => tag.slug && tag.name).map(tag => <Link key={tag.id || tag.slug} to={`/blog/category/${encodeURIComponent(tag.slug)}`} className="text-[var(--primary,#1d4ed8)] hover:underline">{tag.name}</Link>)}
          {post.publishedAt ? <span>{formatDate(post.publishedAt)}</span> : null}
          {post.readingTime ? (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {post.readingTime} min read
            </span>
          ) : null}
        </div>
        <h2 className="mt-3 text-xl font-bold leading-tight text-[var(--text,#0f172a)]">
          <Link to={`/blog/${post.slug}`} className="hover:text-[var(--primary,#1d4ed8)]">{post.title}</Link>
        </h2>
        {post.excerpt ? <p className="mt-3 line-clamp-3 text-sm leading-6 text-[var(--muted,#64748b)]">{post.excerpt}</p> : null}
      </div>
    </article>
  );
}

export default function BlogListPage() {
  const { slug: categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const guideView = searchParams.get('format') === 'guides';
  const appliedSearch = searchParams.get('search') || '';
  const pageInput = searchParams.get('page') || '1';
  const currentPage = /^[1-9]\d*$/.test(pageInput) && Number.isSafeInteger(Number(pageInput)) ? Number(pageInput) : 1;
  const { tenant, spotlightName, tenantLogoConfig, tenantLogoImageUrl } = usePublicTenant();
  const initial = useInitialPublicData("articles");
  const [posts, setPosts] = useState(initial?.documents || []);
  const [category, setCategory] = useState(initial?.category || null);
  const [search, setSearch] = useState(appliedSearch);
  const [total, setTotal] = useState(initial?.total ?? initial?.documents?.length ?? 0);
  const [pageSize, setPageSize] = useState(initial?.pageSize || 20);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState('');

  const pageTitle = useMemo(() => {
    if (guideView) return category?.name ? `${category.name} Local Guides & FAQs` : 'Local Guides & FAQs';
    if (category?.name) return `${category.name} Articles`;
    return 'Spotlight Blog';
  }, [category?.name, guideView]);

  usePublicMetadata(() => {
    if (!tenant) return;
    const path = categorySlug ? `/blog/category/${encodeURIComponent(categorySlug)}` : '/blog';
    const canonicalPath = `${path}${currentPage > 1 ? `?page=${currentPage}` : ''}`;
    const pageSuffix = currentPage > 1 ? ` — Page ${currentPage}` : '';
    const title = category?.name ? `${category.name}${pageSuffix} | ${tenantLogoConfig?.cityName} Stories` : `${tenantLogoConfig?.cityName} Local News & Guides${pageSuffix} | ${spotlightName}`;
    const description = category?.name ? `Read ${category.name.toLowerCase()} stories, news, and guides from ${tenantLogoConfig?.cityName}.` : `Read local stories, business spotlights, community news, and helpful guides from ${tenantLogoConfig?.cityName}.`;
    const metadata = buildPublicPageMetadata({ tenant, pathname: path, canonicalPath, siteName: spotlightName, cityName: tenantLogoConfig?.cityName, logoUrl: tenantLogoImageUrl, title, description, robots: guideView || appliedSearch || error || (categorySlug && !loading && !posts.length) ? 'noindex,follow' : 'index,follow' });
    if (!appliedSearch && !guideView) metadata.jsonLd = [buildWebPageSchema({ url: metadata.canonicalUrl, name: title, description, type: 'CollectionPage' }), buildItemListSchema({ url: metadata.canonicalUrl, name: title, items: posts.map((post) => ({ name: post.title, url: `${tenant.canonicalOrigin}/blog/${encodeURIComponent(post.slug)}` })) }), buildBreadcrumbSchema({ url: metadata.canonicalUrl, items: [{ name: 'Home', url: tenant.canonicalOrigin }, { name: 'Blog', url: `${tenant.canonicalOrigin}/blog` }, ...(category?.name ? [{ name: category.name, url: metadata.canonicalUrl }] : [])] })];
    return (metadata);
  }, [guideView, currentPage, error, loading, appliedSearch, category?.name, categorySlug, posts, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl]);

  useEffect(() => {
    if (initial) { setPosts(initial.documents); setCategory(initial.category); setTotal(initial.total ?? initial.documents.length); setPageSize(initial.pageSize || 20); setError(''); setLoading(false); return; }
    let active = true;
    setLoading(true);
    setError('');
    const loader = categorySlug
      ? fetchBlogCategory(categorySlug, { search: appliedSearch, page: pageInput, format: searchParams.get('format') || undefined })
      : fetchBlogPosts({ search: appliedSearch, page: pageInput, format: searchParams.get('format') || undefined });
    loader
      .then((payload) => {
        if (!active) return;
        setPosts(payload.documents || []);
        setCategory(payload.category || null);
        setTotal(payload.total);
        setPageSize(payload.pageSize);
      })
      .catch((err) => {
        if (!active) return;
        setPosts([]);
        setTotal(0);
        setError(err?.message || 'Unable to load blog posts.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [appliedSearch, pageInput, categorySlug, initial, searchParams]);
  useEffect(() => { setSearch(appliedSearch); }, [appliedSearch]);

  function submitSearch(event) {
    event.preventDefault();
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.delete('page');
      if (search.trim()) next.set('search', search.trim()); else next.delete('search');
      return next;
    });
  }

  const hrefForFormat = guides => {
    const query = new URLSearchParams(searchParams);
    query.delete('page');
    if (guides) query.set('format', 'guides'); else query.delete('format');
    const path = categorySlug ? `/blog/category/${encodeURIComponent(categorySlug)}` : '/blog';
    return `${path}${query.size ? `?${query}` : ''}`;
  };
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hrefForPage = page => {
    const query = new URLSearchParams(searchParams);
    if (page <= 1) query.delete('page'); else query.set('page', String(page));
    const path = categorySlug ? `/blog/category/${encodeURIComponent(categorySlug)}` : '/blog';
    return `${path}${query.size ? `?${query}` : ''}`;
  };

  return (
    <div className="min-h-screen bg-[var(--background,#f8fafc)] py-12 text-[var(--text,#0f172a)]">
      <PublicContainer>
        <div className="mb-8 flex flex-col gap-5 border-b border-[var(--border,#e2e8f0)] pb-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-[var(--primary,#1d4ed8)]">{spotlightName}</p>
            <h1 className="mt-2 text-4xl font-bold leading-tight">{pageTitle}</h1>
            <p className="mt-3 max-w-2xl text-base leading-7 text-[var(--muted,#64748b)]">
              Local stories, business spotlights, and helpful guides from your community.
            </p>
          </div>
          <form onSubmit={submitSearch} className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted,#64748b)]" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search articles" className="h-11 w-full rounded-md border border-[var(--border,#e2e8f0)] bg-white pl-9 pr-3 text-sm outline-none focus:border-[var(--primary,#1d4ed8)]" />
          </form>
        </div>

        <nav aria-label="Article formats" className="mb-6 flex flex-wrap gap-4">
          <Link to={hrefForFormat(false)} aria-current={!guideView ? 'page' : undefined} className="underline">All articles</Link>
          <Link to={hrefForFormat(true)} aria-current={guideView ? 'page' : undefined} className="underline">Local guides & FAQs</Link>
        </nav>
        {guideView ? <p className="mb-6 text-sm text-[var(--muted,#64748b)]">Reviewed city and neighborhood guides and answers to local questions.</p> : null}
        {loading ? (
          <div className="rounded-lg border border-[var(--border,#e2e8f0)] bg-white p-8 text-center text-[var(--muted,#64748b)]">Loading articles...</div>
        ) : error ? (
          <InlineFeedback variant="error" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</InlineFeedback>
        ) : posts.length === 0 ? (
          <div className="rounded-lg border border-[var(--border,#e2e8f0)] bg-white p-8 text-center">
            <h2 className="text-lg font-semibold">{guideView ? 'No local guides published yet' : 'No articles published yet'}</h2>
            <p className="mt-2 text-sm text-[var(--muted,#64748b)]">Check back soon for local stories and guides.</p>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {posts.map((post) => <BlogCard key={post.id} post={post} />)}
          </div>
        )}
        {!loading && !error && totalPages > 1 ? <nav aria-label="Article pagination" className="mt-8 flex items-center justify-between gap-4">
          {currentPage > 1 ? <Link to={hrefForPage(currentPage - 1)} rel="prev" className="underline">Previous articles</Link> : <span />}
          <span>Page {currentPage} of {totalPages}</span>
          {currentPage < totalPages ? <Link to={hrefForPage(currentPage + 1)} rel="next" className="underline">Next articles</Link> : <span />}
        </nav> : null}
      </PublicContainer>
    </div>
  );
}
